import { cn, StatusPill } from '@homecare/ui';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { dayText, weekdayName } from '../../lib/schedule-time';

import { boardState, type BoardCard, type BoardLayout, type BoardRow } from './board-layout';

/**
 * The week, caregivers down the side and days across (slice-03 §8).
 *
 * ## Windowed by hand
 *
 * §25.3's budget is 200 caregivers × 7 days. Rendered whole that is 1,400
 * cells and a few thousand nodes inside each re-render, so only the rows in
 * view — plus a few either side — are in the DOM, with spacer rows standing in
 * for the rest at their real height. Row heights vary with the busiest day in
 * the row, so offsets are a prefix sum and the first visible row is found by
 * binary search rather than by dividing by a constant row height.
 *
 * No library: the whole of it is the forty lines below, and a dependency
 * whose job is this would be a dependency to keep current for forty lines.
 *
 * ## What stays put
 *
 * The day header and the open-shifts lane are sticky and never windowed. Open
 * shifts are first because unfilled work is the thing a coordinator is
 * looking for; scrolling to caregiver 140 must not scroll them out of sight.
 * The scroller is `isolate`, so the z-indexes that keep those pinned stay
 * inside the board — without it the header and the name column painted over
 * the booking dialog.
 *
 * ## For a screen reader
 *
 * A real table: the caregiver is the row header, the day the column header,
 * and `aria-rowcount` / `aria-rowindex` say where in the 200 a row is, so the
 * missing rows read as "not rendered", not "not there".
 */

const CARD_HEIGHT = 64;
const CARD_GAP = 4;
const ROW_PADDING = 12;
const EMPTY_ROW_HEIGHT = 52;
const OVERSCAN = 4;

export const rowHeight = (row: BoardRow): number => {
  const busiest = Math.max(0, ...row.cells.map((cell) => cell.length));
  return busiest === 0
    ? EMPTY_ROW_HEIGHT
    : ROW_PADDING + busiest * CARD_HEIGHT + (busiest - 1) * CARD_GAP;
};

/** The first index whose row ends below `offset`. */
const firstVisible = (ends: readonly number[], offset: number): number => {
  let low = 0;
  let high = ends.length - 1;
  while (low < high) {
    const mid = (low + high) >> 1;
    if ((ends[mid] ?? 0) <= offset) low = mid + 1;
    else high = mid;
  }
  return low;
};

export function BoardGrid({
  layout,
  today,
  onOpen,
  viewportHeight: fixedViewport,
}: {
  layout: BoardLayout;
  today: string;
  onOpen: (visitId: string) => void;
  /** For tests, which have no layout engine to measure with. */
  viewportHeight?: number;
}): ReactNode {
  const scroller = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [measured, setMeasured] = useState(720);
  const viewport = fixedViewport ?? measured;

  useEffect(() => {
    const element = scroller.current;
    if (!element || fixedViewport !== undefined) return;
    const measure = (): void => setMeasured(element.clientHeight || 720);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [fixedViewport]);

  const { heights, ends, total } = useMemo(() => {
    const heights = layout.rows.map(rowHeight);
    const ends: number[] = [];
    let running = 0;
    for (const height of heights) {
      running += height;
      ends.push(running);
    }
    return { heights, ends, total: running };
  }, [layout.rows]);

  const first = Math.max(0, firstVisible(ends, scrollTop) - OVERSCAN);
  const last = Math.min(
    layout.rows.length,
    firstVisible(ends, scrollTop + viewport) + 1 + OVERSCAN,
  );
  const before = first === 0 ? 0 : (ends[first - 1] ?? 0);
  const after = total - (last === 0 ? 0 : (ends[last - 1] ?? 0));

  let frame = 0;
  const onScroll = (): void => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => setScrollTop(scroller.current?.scrollTop ?? 0));
  };

  return (
    <div
      ref={scroller}
      onScroll={onScroll}
      // Focusable so the board scrolls from the keyboard (axe: scrollable-region-focusable).
      tabIndex={0}
      aria-label="Schedule for the week. Scroll for more caregivers."
      className="border-line bg-surface relative isolate max-h-[calc(100vh-15rem)] min-h-[24rem] overflow-auto rounded-lg border"
      data-testid="schedule-board"
    >
      <table
        className="w-full min-w-[64rem] table-fixed border-separate border-spacing-0 text-sm"
        aria-rowcount={layout.rows.length + 2}
      >
        <caption className="sr-only">
          Caregivers down the side, days across. Open shifts are the first row.
        </caption>
        <colgroup>
          <col className="w-48" />
          {layout.days.map((day) => (
            <col key={day} />
          ))}
        </colgroup>
        <thead className="bg-surface sticky top-0 z-20">
          <tr aria-rowindex={1}>
            <th
              scope="col"
              className="bg-surface border-line text-ink-3 sticky left-0 z-30 border-b px-3 py-2 text-left text-xs font-semibold"
            >
              Caregiver
            </th>
            {layout.days.map((day) => (
              <th
                key={day}
                scope="col"
                aria-current={day === today ? 'date' : undefined}
                className={cn(
                  'border-line border-b px-2 py-2 text-left text-xs font-semibold',
                  day === today ? 'text-brand bg-brand-soft' : 'text-ink-2',
                )}
              >
                <span aria-hidden="true">{dayText(day)}</span>
                <span className="sr-only">
                  {weekdayName(day)} {dayText(day).slice(4)}
                  {day === today ? ', today' : ''}
                </span>
              </th>
            ))}
          </tr>
          <Row row={layout.open} index={2} days={layout.days} onOpen={onOpen} label="Open shifts" />
        </thead>
        <tbody>
          {before > 0 && (
            <tr aria-hidden="true" style={{ height: before }}>
              <td colSpan={8} />
            </tr>
          )}
          {layout.rows.slice(first, last).map((row, offset) => (
            <Row
              key={row.caregiver?.id}
              row={row}
              index={first + offset + 3}
              height={heights[first + offset]}
              days={layout.days}
              onOpen={onOpen}
              label={row.caregiver?.name ?? ''}
            />
          ))}
          {after > 0 && (
            <tr aria-hidden="true" style={{ height: after }}>
              <td colSpan={8} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Row({
  row,
  index,
  height,
  days,
  onOpen,
  label,
}: {
  row: BoardRow;
  index: number;
  height?: number | undefined;
  days: string[];
  onOpen: (visitId: string) => void;
  label: string;
}): ReactNode {
  const isOpenLane = row.caregiver === null;
  return (
    <tr
      aria-rowindex={index}
      style={height === undefined ? undefined : { height }}
      data-testid={isOpenLane ? 'open-shifts-row' : 'caregiver-row'}
    >
      <th
        scope="row"
        className={cn(
          'border-line sticky left-0 z-10 border-b px-3 py-2 text-left align-top font-semibold',
          isOpenLane ? 'bg-warning-bg text-warning' : 'bg-surface text-ink',
        )}
      >
        {label}
        {row.caregiver && row.caregiver.status !== 'ACTIVE' && (
          <span className="text-ink-3 block text-xs font-normal">
            {row.caregiver.status.toLowerCase().replace('_', ' ')} · still holds visits
          </span>
        )}
      </th>
      {row.cells.map((cell, at) => (
        <td key={days[at]} className="border-line border-b border-l p-1.5 align-top">
          <div className={cn('grid gap-1', isOpenLane && 'max-h-72 overflow-y-auto')}>
            {cell.map((card) => (
              <VisitCard key={`${card.visit.id}-${card.part}`} card={card} onOpen={onOpen} />
            ))}
          </div>
        </td>
      ))}
    </tr>
  );
}

const TONE_EDGE: Record<string, string> = {
  verified: 'border-l-verified',
  scheduled: 'border-l-scheduled',
  progress: 'border-l-progress',
  warning: 'border-l-warning',
  critical: 'border-l-critical',
  inactive: 'border-l-inactive',
};

function VisitCard({ card, onOpen }: { card: BoardCard; onOpen: (id: string) => void }): ReactNode {
  const { visit, part } = card;
  const state = boardState(visit);
  const continues = part === 'continues';
  const time = continues
    ? `until ${visit.localEndTime}`
    : `${visit.localStartTime}–${visit.localEndTime}`;

  return (
    <button
      type="button"
      onClick={() => onOpen(visit.id)}
      data-testid="visit-card"
      data-visit-id={visit.id}
      data-part={part}
      aria-label={`${continues ? `Continues from ${weekdayName(visit.localDate)}. ` : ''}${
        visit.localStartTime
      } to ${visit.localEndTime}${visit.crossesMidnight ? ' overnight' : ''}, ${visit.clientName}, ${
        visit.serviceCode
      }, ${state.label}`}
      style={{ minHeight: CARD_HEIGHT }}
      className={cn(
        'bg-surface border-line hover:bg-surface-2 focus-visible:outline-brand grid w-full gap-0.5 rounded-md border border-l-4 px-2 py-1 text-left',
        TONE_EDGE[state.tone],
        continues && 'border-dashed',
      )}
    >
      <span className="text-ink truncate text-xs font-semibold">
        {continues && <span aria-hidden="true">↳ </span>}
        {time}
        {visit.crossesMidnight && !continues && (
          <span className="text-ink-3 text-2xs font-normal"> · overnight</span>
        )}
      </span>
      <span className="text-ink-2 truncate text-xs">
        {continues ? `from ${dayText(visit.localDate)} · ` : ''}
        {visit.clientName} · {visit.serviceCode}
      </span>
      {/* The pill on a line of its own: beside the service code it was
          clipped to "SCHEDULEI" in a day column, and a status cut short is
          a word nobody can read. */}
      <StatusPill tone={state.tone} className="justify-self-start px-1.5 py-0.5">
        {state.label}
      </StatusPill>
    </button>
  );
}
